// ReportsTab — the contract workspace's Reports panel: a view toggle, the
// reporting-range chips, and whichever report view is selected.
//
// URL state (`?view=` and `?range=`) is owned here so every report view is
// deep-linkable. Two rules govern it:
//
//   - Defaults are never written. `view=summary` and `range=12m` are what you
//     get with no params at all, so writing them would produce two different
//     URLs for one screen and make "is this the default?" unanswerable from
//     the address bar. Choosing the default deletes the param. The rule lives
//     in reportUrlState so a view's own filter cannot apply a different one.
//   - An unrecognised value falls back but does NOT rewrite the URL. Silently
//     correcting `?view=gantt` to the summary would hide the typo from
//     whoever shared the link; leaving it visible lets them fix the source.
//
// A view's own filters belong to that view — the delivery report owns
// `?recordType=` — but this component reads any such param for the error
// boundary's reset key, so a crash clears when the reader changes what is
// being asked for rather than persisting across an unrelated filter.
//
// A `?range=` set on a focused view survives a round trip through the summary,
// which shows no range control. That is deliberate: the summary does not read
// the param, and clearing it would silently discard the window the reader
// chose the moment they glanced at the summary and came back.
//
// The range chips render only on the focused views. The summary is a
// contract-to-date position assembled from nine domains, several of which
// have no meaningful window, so a range control there would imply a filter it
// does not apply.
//
// "Download PDF" applies to the ACTIVE view with its window and filter, and is
// offered only to the roles that may request an export (the same editor gate
// the commentary editor uses). The dialog is remounted per opening so each
// opening starts with every section selected and a fresh request token.

import { useState } from "react";
import { useSearchParams } from "react-router";
import { clsx } from "clsx";
import { Download } from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import ErrorBoundary from "../../common/ErrorBoundary";
import RangeChips from "./RangeChips";
import SummaryReport from "./SummaryReport";
import DeliveryReport from "./DeliveryReport";
import KpiComplianceReport from "./KpiComplianceReport";
import FinancialReport from "./FinancialReport";
import ReportExportModal from "./ReportExportModal";
import { nextReportParams } from "./reportUrlState";
import {
  DELIVERY_RECORD_TYPES,
  REPORT_RANGES,
  type DeliveryRecordType,
  type ReportRange,
} from "../../../types/report";

const REPORT_VIEWS = ["summary", "delivery", "kpi", "financial"] as const;
type ReportView = (typeof REPORT_VIEWS)[number];

const DEFAULT_VIEW: ReportView = "summary";
const DEFAULT_RANGE: ReportRange = "12m";

const VIEW_LABEL: Record<ReportView, string> = {
  summary: "Summary",
  delivery: "Delivery performance",
  kpi: "KPI compliance",
  financial: "Financial performance",
};

function isReportView(value: string | null): value is ReportView {
  return !!value && (REPORT_VIEWS as readonly string[]).includes(value);
}

function isReportRange(value: string | null): value is ReportRange {
  return !!value && (REPORT_RANGES as readonly string[]).includes(value);
}

function isRecordType(value: string | null): value is DeliveryRecordType {
  return !!value && (DELIVERY_RECORD_TYPES as readonly string[]).includes(value);
}

export interface ReportsTabProps {
  contractId: string;
}

export default function ReportsTab({ contractId }: ReportsTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const canExport = user?.role === "admin" || user?.role === "editor";
  const [exportOpen, setExportOpen] = useState(false);
  const [exportOpenings, setExportOpenings] = useState(0);

  const viewParam = searchParams.get("view");
  const view: ReportView = isReportView(viewParam) ? viewParam : DEFAULT_VIEW;

  const rangeParam = searchParams.get("range");
  const range: ReportRange = isReportRange(rangeParam)
    ? rangeParam
    : DEFAULT_RANGE;

  const recordTypeParam = searchParams.get("recordType");
  const recordType: DeliveryRecordType = isRecordType(recordTypeParam)
    ? recordTypeParam
    : "all";

  function openExport() {
    setExportOpenings((n) => n + 1);
    setExportOpen(true);
  }

  function setParam(key: string, value: string, defaultValue: string) {
    setSearchParams(nextReportParams(searchParams, key, value, defaultValue), {
      replace: true,
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          role="group"
          aria-label="Report view"
          className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-800"
        >
          {REPORT_VIEWS.map((value) => {
            const active = view === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={active}
                onClick={() => setParam("view", value, DEFAULT_VIEW)}
                className={clsx(
                  "inline-flex items-center rounded-md px-3 py-1.5 text-caption font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400",
                  active
                    ? "bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300"
                    : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200",
                )}
              >
                {VIEW_LABEL[value]}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {view !== "summary" && (
            <RangeChips
              value={range}
              onChange={(next) => setParam("range", next, DEFAULT_RANGE)}
            />
          )}
          {canExport && (
            <button
              type="button"
              onClick={openExport}
              className="btn-primary inline-flex items-center gap-1"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Download PDF
            </button>
          )}
        </div>
      </div>

      {canExport && exportOpenings > 0 && (
        <ReportExportModal
          key={exportOpenings}
          contractId={contractId}
          isOpen={exportOpen}
          onClose={() => setExportOpen(false)}
          view={view}
          range={range}
          recordType={recordType}
        />
      )}

      {/* A report reads from every other tab's records at once, so one bad
          row in one domain has more surface to crash on than a single
          register does. The boundary keeps the tab bar and the rest of the
          contract usable if that happens; the reset key clears it when the
          reader moves to a different view. */}
      <ErrorBoundary
        resetKey={`${view}:${range}:${searchParams.get("recordType") ?? ""}`}
        scopeLabel="this report"
      >
        {view === "summary" ? (
          <SummaryReport contractId={contractId} />
        ) : view === "delivery" ? (
          <DeliveryReport contractId={contractId} range={range} />
        ) : view === "kpi" ? (
          <KpiComplianceReport contractId={contractId} range={range} />
        ) : (
          <FinancialReport contractId={contractId} range={range} />
        )}
      </ErrorBoundary>
    </div>
  );
}
